import React, { useMemo, useState } from 'react';
import { deleteSite, saveSite, suggestSite } from '../api/gameBuilderAPI';
import SiteMap from './SiteMap';
import { layoutSite } from './siteLayout';
import { THEME_NAMES } from './siteThemes';

/**
 * The plan's site map: the rooms where its missions happen (backend plan.save_site). One small AI
 * call suggests the rooms, which mission happens where and how the plan is drawn (a floor plan, a
 * warehouse plan or a region map - siteThemes.jsx); the creator renames, moves missions between
 * rooms, reorders rooms (the order sets the plan) and can change the drawing. Players see it as the
 * map view that sets the stage (SitePlayer). `embedded`: inside the game chain (ChainCard), which makes the plan and plays
 * it - here it's only shown and edited.
 */
export default function SiteCard({ plan, onSaved, onPlay, embedded = false }) {
  const site = plan.site;
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const shown = draft || site;
  const layout = useMemo(() => (shown ? layoutSite(shown.rooms, shown.theme) : null), [shown]);
  const ready = plan.missions.filter((m) => plan.status?.[m.id]?.game);
  const preview = Object.fromEntries(plan.missions.map((m) => [m.id, { name: m.name, status: plan.status?.[m.id]?.game ? 'open' : 'unready' }]));
  const names = Object.fromEntries(plan.missions.map((m) => [m.id, m.name]));

  const run = async (label, fn) => {
    setBusy(label);
    setError('');
    try {
      return await fn();
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setBusy('');
    }
  };

  const create = async () => {
    const out = await run('Laying out the rooms… (one small AI call)', () => suggestSite(plan.id));
    if (out) setDraft(out.site);
  };
  const edit = (fn) => setDraft((d) => {
    const next = { ...d, rooms: d.rooms.map((r) => ({ ...r, missions: [...r.missions] })) };
    fn(next);
    return next;
  });
  const moveMission = (id, toRoom) => edit((d) => {
    d.rooms.forEach((r) => { r.missions = r.missions.filter((m) => m !== id); });
    d.rooms.find((r) => r.id === toRoom).missions.push(id);
  });
  const moveRoom = (i, step) => edit((d) => { [d.rooms[i], d.rooms[i + step]] = [d.rooms[i + step], d.rooms[i]]; });
  const addRoom = () => edit((d) => { d.rooms.push({ id: `new${Date.now()}`, name: 'New room', missions: [] }); });
  const removeRoom = (i) => edit((d) => { d.rooms.splice(i, 1); });

  const save = async () => {
    const out = await run('Saving…', () => saveSite(plan.id, draft));
    if (out) { onSaved(out.site); setDraft(null); }
  };
  const remove = async () => {
    const out = await run('Removing…', () => deleteSite(plan.id));
    if (out) onSaved(null);
  };

  return (
    <section className={embedded ? 'gb-site gb-site-embedded' : 'gb-card gb-site'}>
      <div className="gb-plan-head">
        {embedded ? <h3 className="gb-story-title">Floor plan{shown?.name ? `: ${shown.name}` : ''}</h3>
          : <h2 className="gb-story-title">Site map{shown?.name ? `: ${shown.name}` : ''}</h2>}
        {!draft && site && (
          <div className="gb-header-actions">
            {!embedded && <button type="button" className="gb-btn gb-btn-sm" onClick={onPlay} disabled={ready.length === 0}>▶ Play on the map</button>}
            <button type="button" className="gb-btn gb-btn-sm gb-btn-quiet" onClick={() => setDraft({ ...site, rooms: site.rooms.map((r) => ({ ...r, missions: [...r.missions] })) })}>Edit rooms</button>
            {!embedded && <button type="button" className="gb-link" onClick={remove} disabled={!!busy}>Remove</button>}
          </div>
        )}
      </div>
      {embedded && !draft && <p className="gb-small gb-muted">Where a link moves to another room, the player chooses the room on this plan.</p>}

      {!shown && !embedded && (
        <div className="gb-story-empty">
          <p className="gb-muted">Show players a floor plan of the site first: every room and what happens there. They click a room to go and play its mission.</p>
          <button type="button" className="gb-btn gb-btn-sm" onClick={create} disabled={!!busy}>Create the site map</button>
        </div>
      )}

      {shown && (
        <div className="gb-site-preview">
          <SiteMap layout={layout} missions={preview} title={shown.name} />
        </div>
      )}
      {!draft && site?.intro && <p className="gb-muted gb-small">{site.intro}</p>}
      {!embedded && !draft && site && ready.length === 0 && <p className="gb-small gb-muted">Build at least one game to play on the map.</p>}

      {draft && (
        <div className="gb-site-edit">
          <div className="gb-site-fields">
            <label className="gb-field"><span className="gb-small gb-muted">Site</span>
              <input value={draft.name} maxLength={60} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
            <label className="gb-field"><span className="gb-small gb-muted">What players read first</span>
              <input value={draft.intro} maxLength={140} onChange={(e) => setDraft({ ...draft, intro: e.target.value })} /></label>
            <label className="gb-field"><span className="gb-small gb-muted">Drawn as</span>
              <select value={draft.theme || 'floor'} onChange={(e) => setDraft({ ...draft, theme: e.target.value })}>
                {Object.entries(THEME_NAMES).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select></label>
          </div>
          <ol className="gb-rooms">
            {draft.rooms.map((r, i) => (
              <li key={r.id} className="gb-room-row">
                <div className="gb-room-head">
                  <input className="gb-link-label" value={r.name} maxLength={30} aria-label="Room name"
                    onChange={(e) => edit((d) => { d.rooms[i].name = e.target.value; })} />
                  <span className="gb-chain-tools">
                    <button type="button" className="gb-icon-btn" aria-label={`Move ${r.name} earlier`} disabled={i === 0} onClick={() => moveRoom(i, -1)}>↑</button>
                    <button type="button" className="gb-icon-btn" aria-label={`Move ${r.name} later`} disabled={i === draft.rooms.length - 1} onClick={() => moveRoom(i, 1)}>↓</button>
                    <button type="button" className="gb-icon-btn" aria-label={`Remove ${r.name}`} disabled={r.missions.length > 0}
                      title={r.missions.length ? 'Move its missions to another room first' : ''} onClick={() => removeRoom(i)}>✕</button>
                  </span>
                </div>
                {r.missions.map((id) => (
                  <div key={id} className="gb-room-mission">
                    <span><span className="gb-mid">{id}</span> {names[id]}</span>
                    <select value={r.id} aria-label={`Room for ${names[id]}`} onChange={(e) => moveMission(id, e.target.value)}>
                      {draft.rooms.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  </div>
                ))}
                {r.missions.length === 0 && <p className="gb-small gb-muted">No missions here.</p>}
              </li>
            ))}
          </ol>
          <div className="gb-actions">
            <button type="button" className="gb-btn gb-btn-sm gb-btn-quiet" onClick={addRoom} disabled={draft.rooms.length >= 8}>+ Room</button>
            <button type="button" className="gb-btn gb-btn-sm" onClick={save} disabled={!!busy || draft.rooms.filter((r) => r.missions.length).length < 2}>Save the rooms</button>
            <button type="button" className="gb-btn gb-btn-sm gb-btn-quiet" onClick={() => { setDraft(null); setError(''); }} disabled={!!busy}>Cancel</button>
          </div>
        </div>
      )}
      {busy && <p className="gb-progress"><span className="gb-spinner" /> {busy}</p>}
      {error && <div className="gb-error" role="alert">{error}</div>}
    </section>
  );
}
